import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import DetailViewIcon from '@atlaskit/icon/glyph/detail-view';
import PresenceActiveIcon from '@atlaskit/icon/glyph/presence-active';
import { Box, xcss } from '@atlaskit/primitives';
import { N500, N10 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { isFedRamp } from '@atlassian/atl-context';
import { getTheme } from '@atlassian/jira-common-theme';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { UserPropertiesContainer } from '@atlassian/jira-insights-common/src/services/user-properties';
import {
	useNextBestTaskPanel,
	ERROR_BOUNDARY_PACKAGE,
	NEXT_BEST_TASK_ANALYTICS,
	useNextBestTaskContainer,
} from '@atlassian/jira-insights-next-best-task';
import {
	NEXT_BEST_TASK_PANEL_ISDISABLED_USERPROPERTY_KEY,
	NEXT_BEST_TASK_PANEL_LAST_OPENED_TASK_ID_USERPROPERTY_KEY,
	NEXT_BEST_TASK_USER_PROPERTIES_SCOPE,
} from '@atlassian/jira-insights-next-best-task/src/common/constants';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	FireUiAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import { useAccountId } from '@atlassian/jira-tenant-context-controller';

import messages from './messages';
import { NextBestTaskSpotlight } from './spotlight';

const BUTTON_CONTAINER_WIDTH = 46;

const NotificationBubble = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'rendered',
				actionSubject: 'icon',
			}),
			`${NEXT_BEST_TASK_ANALYTICS}.notificationBubble.rendered`,
		);
	}, [createAnalyticsEvent]);

	return (
		<PresenceActionIconWrapper data-testid="insights-next-best-task-filter-button.ui.presence-action-icon-wrapper">
			<PresenceActionIconBorder>
				<PresenceActiveIcon
					label={formatMessage(messages.presenceActionIconLabelNonfinal)}
					primaryColor={token('color.icon.accent.blue', '#1D7AFC')}
					size="small"
				/>
			</PresenceActionIconBorder>
		</PresenceActionIconWrapper>
	);
};

export const NextBestTaskButton = ({ showBubble = false }: { showBubble?: boolean }) => {
	const { isOpen, isControlVisible, isNbtToggleButtonVisible, onTogglePanelVisibility } =
		useNextBestTaskPanel();

	const onClick = useCallback(
		(_: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			const previousIsOpen = isOpen;
			onTogglePanelVisibility();

			fireUIAnalytics(analyticsEvent, `${NEXT_BEST_TASK_ANALYTICS}.panelButton`, {
				panelIsOpen: !previousIsOpen,
				previousPanelIsOpen: previousIsOpen,
				previousButtonShowBubble: !previousIsOpen && showBubble,
			});
		},
		[isOpen, onTogglePanelVisibility, showBubble],
	);

	return (
		<AnimatingButtonContainer
			isVisible={!!isControlVisible && isNbtToggleButtonVisible}
			isPanelOpen={isOpen}
		>
			<NBTButtonMain isAnimated isOpen={isOpen} onClick={onClick} showBubble={showBubble} />
		</AnimatingButtonContainer>
	);
};

const NBTButtonMain = ({
	isAnimated,
	isOpen,
	showBubble,
	onClick,
}: {
	isAnimated: boolean;
	isOpen: boolean;
	showBubble: boolean;
	onClick: (_: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => void;
}) => {
	const { formatMessage } = useIntl();
	return (
		<ButtonWrapper isFixedWidth={!isAnimated}>
			{!isAnimated && (
				<FireUiAnalytics
					action="render"
					actionSubject="button"
					actionSubjectId={`${NEXT_BEST_TASK_ANALYTICS}.panelButton`}
					attributes={{
						panelIsOpen: isOpen,
					}}
				/>
			)}
			<Tooltip content={formatMessage(messages.buttonTooltip)}>
				<NextBestTaskSpotlight>
					<CustomThemeButton
						aria-label={formatMessage(messages.buttonAriaLabel)}
						isSelected={isOpen}
						onClick={onClick}
						iconBefore={
							<DetailViewIcon
								label={formatMessage(messages.buttonAriaLabel)}
								primaryColor={isOpen ? undefined : token('color.icon', N500)}
							/>
						}
						theme={getTheme({
							buttonStyles: {
								default: {
									padding: `${token('space.0', '0px')} ${token(
										'space.075',
										'6px',
									)} ${token('space.0', '0px')} ${token('space.150', '12px')}`,
									borderRadius: '0 20px 20px 0',
								},
							},
						})}
					/>
					{!isOpen && showBubble ? <NotificationBubble /> : null}
				</NextBestTaskSpotlight>
			</Tooltip>
		</ButtonWrapper>
	);
};

// This whole file will be removed when we cleanup 'jira.work-suggestions.remove-nbt-button' FF
// Intentionally not feature-flagging this component because it will never be rendered when FF is true
const preloadPropertyKeys = [
	NEXT_BEST_TASK_PANEL_ISDISABLED_USERPROPERTY_KEY,
	NEXT_BEST_TASK_PANEL_LAST_OPENED_TASK_ID_USERPROPERTY_KEY,
];

export const ConnectedNextBestTaskButton = () => {
	const accountId = useAccountId();
	if (!accountId) throw new Error('accountId could not be found');
	const { isControlVisible } = useNextBestTaskPanel();
	const [{ showBubble }] = useNextBestTaskContainer();

	if (!isControlVisible) {
		return null;
	}

	return (
		<UserPropertiesContainer
			scope={NEXT_BEST_TASK_USER_PROPERTIES_SCOPE}
			accountId={accountId}
			preloadPropertyKeys={preloadPropertyKeys}
		>
			<NextBestTaskButton showBubble={showBubble} />
		</UserPropertiesContainer>
	);
};

export const SafeNextBestTaskButton = () => (
	<JSErrorBoundary
		id="next-best-task-button"
		packageName={ERROR_BOUNDARY_PACKAGE}
		fallback="unmount"
	>
		{!isFedRamp() && <ConnectedNextBestTaskButton />}
	</JSErrorBoundary>
);

const AnimatingButtonContainer = ({
	isVisible,
	isPanelOpen,
	children,
}: {
	isVisible: boolean;
	isPanelOpen: boolean;
	children: ReactNode;
}) => {
	const [showOverflow, setShowOverflow] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (isVisible && !showOverflow) {
			setShowOverflow(true);
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'render',
					actionSubject: 'button',
				}),
				`${NEXT_BEST_TASK_ANALYTICS}.panelButton`,
				{
					panelIsOpen: isPanelOpen,
				},
			);
		}
	}, [isVisible, showOverflow, createAnalyticsEvent, isPanelOpen]);

	const transitionEndHandler = () => {
		if (!isVisible && showOverflow) {
			setShowOverflow(false);
		}
	};

	return (
		<Box
			testId="insights-next-best-task-filter-button.ui.animating-button-container"
			xcss={[
				buttonTransitionStylingStyles,
				isVisible ? enteringButtonWidthStyles : exitingButtonWidthStyles,
				showOverflow ? overflowVisibleStyles : overflowHiddenStyles,
			]}
			onTransitionEnd={transitionEndHandler}
		>
			{children}
		</Box>
	);
};

const buttonTransitionStylingStyles = xcss({ transition: 'width 0.3s ease' });

const enteringButtonWidthStyles = xcss({
	width: `${BUTTON_CONTAINER_WIDTH}px`,
});

const exitingButtonWidthStyles = xcss({
	width: '0px',
});

const overflowHiddenStyles = xcss({ overflow: 'hidden' });
const overflowVisibleStyles = xcss({ overflow: 'visible' });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div<{ isFixedWidth: boolean }>({
	display: 'inline-flex',
	flexShrink: 0,
	position: 'relative',
	alignItems: 'center',
	marginLeft: token('space.negative.150', '-12px'),
	width: ({ isFixedWidth }) => (isFixedWidth ? `${BUTTON_CONTAINER_WIDTH}px` : 'inherit'),
	marginRight: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PresenceActionIconBorder = styled.div({
	backgroundColor: token('color.background.input.hovered', N10),
	width: '12px',
	height: '12px',
	borderRadius: '50%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PresenceActionIconWrapper = styled.div({
	pointerEvents: 'none',
	display: 'flex',
	position: 'absolute',
	alignItems: 'center',
	justifyContent: 'center',
	height: '23px',
	width: '23px',
	top: token('space.0', '0px'),
	right: token('space.0', '0px'),
});
